import React, { useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal as RNModal, Image } from 'react-native';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiUrl } from './utils/config';

interface ModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectOption: (withImages: boolean, chapters: number) => void;
  tokenBalance: number;
  userId: string | null;
}

const chapterOptions = [7, 15, 30];

const Modal: React.FC<ModalProps> = ({
  isVisible,
  onClose,
  onSelectOption,
  tokenBalance,
  userId,
}) => {
  const [selectedChapters, setSelectedChapters] = useState<number>(7);
  const [withImages, setWithImages] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');

  const noImagesCost = selectedChapters === 7 ? 1 : selectedChapters === 15 ? 2 : 4;
  const withImagesCost = noImagesCost * 2;

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      const id_token = session?.tokens?.idToken;
      return id_token || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const updateTokens = async (tokenChange: number) => {
    try {
      const authToken = await getAuthToken();
      if (!authToken) throw new Error('Unable to retrieve auth token');

      const response = await fetch(`${apiUrl}tokens/spend-tokens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id: userId,
          token_balance: tokenBalance,
          token_change: tokenChange,
        }),
      });

      if (!response.ok) throw new Error('Failed to update tokens');
      const data = await response.json();
      console.log('Tokens updated successfully:', data);
    } catch (error) {
      console.error('Error updating tokens:', error);
    }
  };

  const handleSubmit = async () => {
    const cost = withImages ? withImagesCost : noImagesCost;
    if (tokenBalance < cost) {
      setErrorMessage('You do not have enough tokens to proceed.');
      return;
    }

    await updateTokens(-cost);
    onSelectOption(withImages, selectedChapters);
  };

  if (!isVisible) return null;

  return (
    <RNModal transparent visible={isVisible} animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <Text style={styles.title}>Select Story Options</Text>

          <Text style={styles.label}>How many Chapters do you want in your story?</Text>
          <View style={styles.optionRow}>
            <Image source={require('./assets/book-icon.png')} style={styles.icon} />
              <View style={styles.optionSelect}>
                {chapterOptions.map((num) => (
                  <TouchableOpacity
                    key={num}
                    style={[
                      styles.choice,
                      selectedChapters === num && styles.choiceSelected,
                    ]}
                    onPress={() => setSelectedChapters(num)}
                  >
                    <Text
                      style={[
                        styles.choiceText,
                        selectedChapters === num && styles.choiceTextSelected,
                      ]}
                    >
                      {num}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
          </View>

          <Text style={[styles.label, { marginTop: 24 }]}>Do you want images in your story?</Text>
          <View style={styles.optionRow}>
            <Image source={require('./assets/image-icon.png')} style={styles.icon} />
            <View style={styles.optionSelect}>
                {[false, true].map((val, i) => (
                  <TouchableOpacity
                    key={i}
                    style={[
                      styles.choice,
                      withImages === val && styles.choiceSelected,
                    ]}
                    onPress={() => setWithImages(val)}
                  >
                    <Text
                      style={[
                        styles.choiceText,
                        withImages === val && styles.choiceTextSelected,
                      ]}
                    >
                      {val ? '✓' : '✕'}
                    </Text>
                  </TouchableOpacity>
                ))}
            </View>
          </View>

          {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}

          <TouchableOpacity style={styles.submitBtn} onPress={handleSubmit}>
            <Text style={styles.submitText}>
              Spend {withImages ? withImagesCost : noImagesCost} Tokens
            </Text>
          </TouchableOpacity>

          <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
            <Text style={styles.closeText}>×</Text>
          </TouchableOpacity>
        </View>
      </View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    width: 320,
    backgroundColor: '#D9D9D9',
    borderRadius: 20,
    padding: 20,
    alignItems: 'center',
    position: 'relative',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#160014',
    marginBottom: 20,
    fontFamily: 'Futura',
  },
  label: {
    fontSize: 16,
    color: '#160014',
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: 'Futura',
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 12,
  },
  optionSelect: {
    flexDirection: 'row',
    backgroundColor: '#ECECEC',
    borderRadius: 999,
  },
  icon: {
    width: 25,
    height: 25,
    resizeMode: 'contain',
    marginRight: 6,
    color: '#544564'
  },
  choice: {
    backgroundColor: '#ECECEC',
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 16,
  },
  choiceSelected: {
    backgroundColor: '#534466',
    borderColor: '#534466',
  },
  choiceText: {
    fontSize: 14,
    color: '#666',
    fontWeight: 'bold',
  },
  choiceTextSelected: {
    color: '#fff',
  },
  submitBtn: {
    backgroundColor: '#534466',
    paddingHorizontal: 30,
    paddingVertical: 10,
    borderRadius: 999,
    marginTop: 20,
  },
  submitText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  closeBtn: {
    position: 'absolute',
    top: 10,
    right: 14,
  },
  closeText: {
    fontSize: 20,
    color: '#534466',
  },
  error: {
    color: 'red',
    marginTop: 10,
    fontSize: 12,
  },
});

export default Modal;
